import { useState, useRef, useEffect } from 'react';
import '../App.css';
import axios from 'axios';
import FrameList from './FrameList';
import MatList from './MatList';
import Swal from 'sweetalert2';
import { totalPrice } from './PricingEngine';

function FrameYourPaperArt({ image, onEditImage }) {
  /* ================= IMAGE ================= */
  const [userImage, setUserImage] = useState(() => {
    if (image) return image;

    const stored = localStorage.getItem('opticon_last_image');
    return stored || null;
  });
  const fileInputRef = useRef(null);
  /* ================= SIZE ================= */
  const [width, setWidth] = useState(30);
  const [height, setHeight] = useState(30);
  const [imageAspect, setImageAspect] = useState(null);
  const [sizeMode, setSizeMode] = useState('manual');
  const [imagePxSize, setImagePxSize] = useState(null);
  /* ================= IMAGE SIZE DISPLAY ================= */
  const PRINT_DPI = 300;
  const pxToCm = px => ((px / PRINT_DPI) * 2.54).toFixed(1);

  /* ================= STRETCH ================= */
  const [stretchMode, setStretchMode] = useState('no'); // yes / no
  const [stretchType, setStretchType] = useState('no');
  const [imgPos, setImgPos] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  const getStretchValue = () => {
    switch (stretchType) {
      case 'small': return 1.02;      // up to 40 cm
      case 'medium': return 1.05;     // 41 to 80 cm
      case 'large': return 1.08;      // 81 to 120 cm
      case 'extra-large': return 1.12;     // 121 to 150 cm
      default: return 1;             // no stretch
    }
  };
  /* ================= FRAME ================= */
  const [frameOpen, setFrameOpen] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [shipArt, setShipArt] = useState('no');
  /* ================= MAT ================= */
  const [matOpen, setMatOpen] = useState(false);
  const [activeMatIndex, setActiveMatIndex] = useState(0);
  const MAT_PRICE_PER_CM = 1.32;
  const [mats, setMats] = useState([
    {
      mat: null,
      mode: 'equal', // 'equal' | 'custom'
      thickness: 0,  // used for equal
      custom: { top: 0, bottom: 0, left: 0, right: 0 } // used for custom
    },
    {
      mat: null,
      mode: 'equal',
      thickness: 0,
      custom: { top: 0, bottom: 0, left: 0, right: 0 }
    },
    {
      mat: null,
      mode: 'equal',
      thickness: 0,
      custom: { top: 0, bottom: 0, left: 0, right: 0 }
    }
  ]);
  const MAT_MIN_CM = 1;
  const MAT_MAX_CM = 15;
  const MAT_STEP_CM = 1;

  const [vGrooveOpen, setVGrooveOpen] = useState(false);
  const [selectedVGroove, setSelectedVGroove] = useState(null);
  const vGrooveOptions = [
    { id: 'no', label: 'No V-Groove' },
    { id: 'single v-groove', label: 'Single V-Groove' },
    { id: 'double v-groove', label: 'Double V-Groove' },
  ];
  const [vGrooveType, setVGrooveType] = useState('no');

  /* ================= GLASS ================= */
  const [glassType, setGlassType] = useState('none');
  const glassOptions = [
    { id: 'none', label: 'No Glass' },
    { id: 'standard_glass', label: 'Standard Glass' },
    { id: 'museum_uv_glass', label: 'Museum UV Non Reflective Glass' },
    { id: 'clear_perspex', label: 'Clear Perspex' }
  ];

  /* ================= SPACER ================= */
  const spacerOptions = [
    { id: 'no', label: 'No Spacer' },
    { id: 'small', label: 'Small' },
    { id: 'medium', label: 'Medium' },
    { id: 'large', label: 'Large' }
  ];
  // State to track selected spacer
  const [spacerType, setSpacerType] = useState('no');
  // Compute selected spacer price

  const [backingType, setBackingType] = useState('none');
  const backingOptions = [
    { id: 'none', label: 'No Backing' },
    { id: 'adhesive_foamcore', label: 'Adhesive Foamcore' },
    { id: 'non_adhesive_foamcore', label: 'Non-Adhesive Foamcore' }
  ];

  const updateMat = (index, data) => {
    setMats(prev => {
      const copy = [...prev];

      copy[index] = {
        mat: copy[index]?.mat || null,
        mode: copy[index]?.mode || 'equal',
        thickness: copy[index]?.thickness || 0,
        custom: copy[index]?.custom || { top: 0, bottom: 0, left: 0, right: 0 },
        ...data
      };

      return copy;
    });
  };

  const hasMat = index => mats[index]?.mat !== null;


  /* ================= CANVAS ================= */
  const canvasRef = useRef(null);

  /* ================= LOADER ================= */
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sizeTouched, setSizeTouched] = useState(false);

  /* ================= IMAGE UPLOAD ================= */
  const handleImageUpload = (e) => {
    if (!e.target.files?.length) return;

    const file = e.target.files[0];

    // ✅ Validate image type
    if (!file.type.startsWith('image/')) return;

    const MAX_MB = 64;

    // ✅ Validate size (64MB limit)
    if (file.size > MAX_MB * 1024 * 1024) {
      Swal.fire({
        icon: 'error',
        title: 'Image too large',
        text: 'Please upload an image under 64MB.',
        confirmButtonText: 'OK'
      });
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();

      img.onload = () => {
        const imgW = img.naturalWidth;
        const imgH = img.naturalHeight;

        setImagePxSize({ w: imgW, h: imgH });

        // AUTO size from image (only initially)
        const aspect = imgW / imgH;
        const defaultW = 30;
        const defaultH = Math.round(defaultW / aspect);

        setWidth(defaultW);
        setHeight(defaultH);
        setSizeTouched(false);

        setUserImage(reader.result);
        localStorage.setItem('opticon_last_image', reader.result);
      };

      img.src = reader.result;
    };

    reader.readAsDataURL(file);
  };

  /* ================= FRAME OPTIONS ================= */
  const frameOptions = FrameList;

  /* ================= MAT OPTIONS ================= */
  const matOptions = MatList;

  /* ================= ENGINE PRICING ================= */
  const CM_TO_MM = 10;
  const DEFAULT_SHEET_WIDTH_MM = 1000;
  const DEFAULT_SHEET_HEIGHT_MM = 1500;

  const selectedMatLayer = mats.find(m => m?.mat);
  const selectedMat = selectedMatLayer?.mat || null;

  const buildEngineMat = () => {
    if (!selectedMatLayer || !selectedMat) return {};

    if (selectedMatLayer.mode === 'custom') {
      const top = Number(selectedMatLayer.custom?.top || 0);
      const bottom = Number(selectedMatLayer.custom?.bottom || 0);
      const left = Number(selectedMatLayer.custom?.left || 0);
      const right = Number(selectedMatLayer.custom?.right || 0);
      const side = (left + right) / 2;

      return {
        ...selectedMat,
        top: top * CM_TO_MM,
        bottom: bottom * CM_TO_MM,
        side: side * CM_TO_MM,
        width: selectedMat.width || DEFAULT_SHEET_WIDTH_MM,
        height: selectedMat.height || DEFAULT_SHEET_HEIGHT_MM,
        sheet_price: selectedMat.sheet_price || selectedMat.price
      };
    }

    const thicknessCm = Number(selectedMatLayer.thickness || 0);
    const sideMm = thicknessCm * CM_TO_MM;

    return {
      ...selectedMat,
      top: sideMm,
      bottom: sideMm,
      side: sideMm,
      width: selectedMat.width || DEFAULT_SHEET_WIDTH_MM,
      height: selectedMat.height || DEFAULT_SHEET_HEIGHT_MM,
      sheet_price: selectedMat.sheet_price || selectedMat.price
    };
  };

  const selectedGlass = glassOptions.find(g => g.id === glassType);
  const selectedBacking = backingOptions.find(b => b.id === backingType);

  const buildSheetMaterial = item => {
    if (!item || item.id === 'none') return {};
    return {
      ...item,
      width: item.width || DEFAULT_SHEET_WIDTH_MM,
      height: item.height || DEFAULT_SHEET_HEIGHT_MM,
      sheet_price: item.sheet_price || item.price
    };
  };

  const pricing = totalPrice({
    qty: 1,
    opening_width: width * CM_TO_MM,
    opening_height: height * CM_TO_MM,
    frame: selectedFrame || {},
    mat: buildEngineMat(),
    glass: buildSheetMaterial(selectedGlass),
    backing: buildSheetMaterial(selectedBacking),
    extras: [
      { type: 'v_groove', option: vGrooveType },
      { type: 'spacer', option: spacerType }
    ],
    openings: 1,
    discount: 0
  });

  const basePrice = pricing.basePrice;
  const finalPrice = pricing.finalPrice;

  /* ================= GST ================= */
  const GST_RATE = 0.1;

  const gstAmount = finalPrice * GST_RATE;
  const totalWithGST = finalPrice + gstAmount;

  /* ================= CANVAS RENDER ================= */
  const DEFAULT_CANVAS_WIDTH = 520;
  const DEFAULT_CANVAS_HEIGHT = 360;

  const updateCanvasSize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const PREVIEW_PPCM = 20;
    const PREVIEW_PPPX = 0.15;

    let previewW, previewH;

    previewW = width * PREVIEW_PPCM;
    previewH = height * PREVIEW_PPCM;

    canvas.width = Math.round(previewW);
    canvas.height = Math.round(previewH);
  };

  const handleMouseDown = (e) => {
    isDragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;

    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;

    setImgPos(prev => {
      let newX = prev.x + dx;
      let newY = prev.y + dy;

      // 🔒 clamp inside mat opening
      const canvas = canvasRef.current;
      const W = canvas.width;
      const H = canvas.height;

      const baseW = W;
      const baseH = H;

      const stretchValue = getStretchValue();
      const limitX = (baseW * (stretchValue - 1)) / 2;
      const limitY = (baseH * (stretchValue - 1)) / 2;

      newX = Math.max(-limitX, Math.min(limitX, newX));
      newY = Math.max(-limitY, Math.min(limitY, newY));

      newX = Math.max(-limitX, Math.min(limitX, newX));
      newY = Math.max(-limitY, Math.min(limitY, newY));

      return { x: newX, y: newY };
    });

    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const renderCanvas = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;

    ctx.clearRect(0, 0, W, H);

    /* ================= UNITS ================= */
    const PREVIEW_PX_PER_CM = 10;

    // Frame thickness comes from backend in mm → convert to cm → preview px
    const frameTpx =
      ((selectedFrame?.thickness || 0) / 10) * PREVIEW_PX_PER_CM;

    /* ================= SAFETY CLAMP ================= */
    const maxInset = Math.min(W, H) / 2 - 4;
    let insetPx = Math.min(frameTpx, maxInset);

    /* ================= MULTI MATS ================= */
    for (const m of mats) {
      if (!m.mat) continue;

      let topPx, bottomPx, leftPx, rightPx;

      if (m.mode === 'equal') {
        const px = m.thickness * PREVIEW_PX_PER_CM;
        topPx = bottomPx = leftPx = rightPx = px;
      } else {
        topPx = m.custom.top * PREVIEW_PX_PER_CM;
        bottomPx = m.custom.bottom * PREVIEW_PX_PER_CM;
        leftPx = m.custom.left * PREVIEW_PX_PER_CM;
        rightPx = m.custom.right * PREVIEW_PX_PER_CM;
      }

      const ox = insetPx + leftPx;
      const oy = insetPx + topPx;

      const outerX = insetPx;
      const outerY = insetPx;
      const outerW = W - insetPx * 2;
      const outerH = H - insetPx * 2;

      if (m.mat.img) {
        const matImg = new Image();
        matImg.src = m.mat.img;
        await new Promise(res => (matImg.onload = res));
        ctx.fillStyle = ctx.createPattern(matImg, 'repeat');
      } else {
        ctx.fillStyle = '#f4f4f4';
      }

      ctx.fillRect(outerX, outerY, outerW, outerH);

      ctx.clearRect(
        insetPx + leftPx,
        insetPx + topPx,
        W - insetPx * 2 - leftPx - rightPx,
        H - insetPx * 2 - topPx - bottomPx
      );

      insetPx += Math.max(topPx, bottomPx, leftPx, rightPx);
    }


    /* ================= IMAGE ================= */
    if (userImage) {
      const img = new Image();
      img.src = userImage;
      await new Promise(res => (img.onload = res));

      let baseW = W - insetPx * 2;
      let baseH = H - insetPx * 2;

      let drawW = baseW;
      let drawH = baseH;

      let offsetX = insetPx;
      let offsetY = insetPx;

      const stretchValue = getStretchValue();

      if (stretchValue !== 1) {
        drawW = baseW * stretchValue;
        drawH = baseH * stretchValue;

        offsetX = insetPx - (drawW - baseW) / 2;
        offsetY = insetPx - (drawH - baseH) / 2;
      }

      // ✅ CLIP to mat opening
      ctx.save();

      ctx.beginPath();
      ctx.rect(
        insetPx,
        insetPx,
        baseW,
        baseH
      );
      ctx.clip();

      ctx.drawImage(
        img,
        offsetX + imgPos.x,
        offsetY + imgPos.y,
        drawW,
        drawH
      );

      ctx.restore();
    }

    /* ================= FRAME ================= */
    if (selectedFrame?.img && frameTpx > 0) {
      const frameImg = new Image();
      frameImg.src = selectedFrame.img;
      await new Promise(res => (frameImg.onload = res));

      // Top
      ctx.drawImage(frameImg, 0, 0, W, frameTpx);
      // Bottom
      ctx.drawImage(frameImg, 0, H - frameTpx, W, frameTpx);
      // Left
      ctx.drawImage(frameImg, 0, 0, frameTpx, H);
      // Right
      ctx.drawImage(frameImg, W - frameTpx, 0, frameTpx, H);
    }
  };


  /* ================= ADD TO CART ================= */
  const handleAddToCart = async () => {
    if (!canvasRef.current) return;

    setLoading(true);
    setProgress(0);

    try {

      let imageToSend = '';

      if (stretchMode === 'yes') {
        // send stretched version
        imageToSend = canvasRef.current.toDataURL('image/png');
      } else {
        // send original image
        if (userImage) {
          imageToSend = userImage;
        } else {
          imageToSend = canvasRef.current.toDataURL('image/png');
        }
      }

      /* ================= MULTI MATS ================= */
      const matMeta = [];

      mats.forEach((m, index) => {
        if (!m.mat) return;

        const i = index + 1;

        // Always send mat name
        matMeta.push({
          key: `mat_${i}_name`,
          value: m.mat.name
        });

        // Send mode
        matMeta.push({
          key: `mat_${i}_mode`,
          value: m.mode
        });

        if (m.mode === 'equal') {

          // Equal thickness (single value)
          matMeta.push({
            key: `mat_${i}_thickness_mm`,
            value: m.thickness * 10 // cm → mm
          });

        } else {

          // Custom 4-side thickness
          ['top', 'bottom', 'left', 'right'].forEach(side => {
            matMeta.push({
              key: `mat_${i}_${side}_mm`,
              value: m.custom[side] * 10 // cm → mm
            });
          });

        }
      });

      /* ================= PAYLOAD ================= */

      const payload = {
        id: 8520,
        quantity: 1,
        meta_data: [
          { key: 'frame_name', value: selectedFrame?.name || '' },
          { key: 'v_groove', value: vGrooveType },
          { key: 'spacer', value: spacerType },
          { key: 'backing_type', value: backingType },
          { key: 'glass_type', value: glassType },
          { key: 'ship_your_art', value: shipArt },
          { key: 'total_price', value: totalWithGST },
          ...matMeta,
          { key: 'stretch_value', value: stretchType },
          { key: 'edited_image', value: imageToSend }
        ]
      };
      console.log('Payload to send:', payload);
      await axios.post(
        'https://opticonframing.com.au/wp-json/opticon/v1/frame-your-paper-art/add-to-cart',
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          onUploadProgress: e => {
            if (!e.total) return;
            setProgress(Math.round((e.loaded * 100) / e.total));
          }
        }
      );

      setProgress(100);

      setTimeout(() => {
        window.location.href = 'https://opticonframing.com.au/cart';
      }, 700);

    } catch (err) {
      console.error(err);
      alert('Add to cart failed');
      setLoading(false);
      setProgress(0);
    }
  };

  const handleRemoveImage = () => {
    setUserImage(null);
    setImageAspect(null);
    setImagePxSize(null); // 🔑 reset image sizing

    setSizeMode('manual');
    setWidth(30);
    setHeight(30);
    setSizeTouched(false);

    localStorage.removeItem('opticon_last_image');
  };

  useEffect(() => {
    (async () => {
      updateCanvasSize();
      renderCanvas();
    })();
  }, [userImage, selectedFrame, width, height, mats, stretchType, imgPos]);


  useEffect(() => {
    if (!selectedFrame && frameOptions.length > 0) {
      setSelectedFrame(frameOptions[0]);
    }
  }, [selectedFrame, frameOptions]);

  useEffect(() => {
    if (!userImage || imagePxSize) return;

    const img = new Image();
    img.onload = () => {
      setImageAspect(img.naturalWidth / img.naturalHeight);

      setImagePxSize({
        w: img.naturalWidth,
        h: img.naturalHeight
      });

      setSizeMode('image'); // 🔑 force image-driven sizing
    };

    img.src = userImage;
  }, [userImage, imagePxSize]);

  useEffect(() => {
    if (image) setUserImage(image);
  }, [image]);
  /* ================= UI ================= */
  return (
    <div className="product-page">

      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          {/* Circular Loader */}
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            border: '5px solid rgba(255,255,255,0.1)',
            borderTop: '5px solid #1E5FFF', // Requested Color
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>

          {/* Percentage Text */}
          <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
            {progress}%
          </p>

          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}

      {/* LEFT */}
      <div className="product-image-panel">
        <canvas
          ref={canvasRef}
          className="real-canvas-preview"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
      </div>

      {/* RIGHT */}
      <div className="product-info-panel">
        <h1 className="product-title">Frame Your Paper Art</h1>

        {/* CANVAS SIZE */}
        <div className="print-option-row">
          <label className="form-label">Canvas Size (cm)</label>

          <div className="image-size-row">
            <input
              type="number"
              value={width}
              onChange={e => {
                setWidth(Number(e.target.value));
                setSizeTouched(true);
                setSizeMode('manual');
              }}
              className="form-select"
            />

            <input
              type="number"
              value={height}
              onChange={e => {
                setHeight(Number(e.target.value));
                setSizeTouched(true);
                setSizeMode('manual');
              }}
              className="form-select"
            />
          </div>
        </div>

        {/* UPLOAD DIGITAL CANVAS IMAGE */}
        <div className="print-option-row">
          <label className="form-label">Upload Digital Canvas Image</label>

          <div className="image-size-row">
            <button
              type="button"
              className="btn-secondary image-upload-btn"
              onClick={() => fileInputRef.current.click()}
            >
              {userImage ? 'Change Image' : 'Add Image'}
            </button>

            {userImage && (
              <button
                className="btn-danger image-remove-btn"
                onClick={handleRemoveImage}
                type="button"
              >
                Remove Image
              </button>
            )}

            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              hidden
              onChange={handleImageUpload}
            />
          </div>
        </div>

        {/* SHIP YOUR ART */}
        <div className="print-option-row">
          <label className="form-label">Ship your art to us</label>

          <select
            className="form-select"
            value={shipArt}
            onChange={e => setShipArt(e.target.value)}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>

        {/* STRETCH IMAGE */}
        <div className="print-option-row">
          <label className="form-label">Stretch Image</label>

          <select
            className="form-select"
            value={stretchType}
            onChange={e => {
              const value = e.target.value;
              setStretchType(value);

              // reset position when no stretch
              if (value === 'no') {
                setImgPos({ x: 0, y: 0 });
              }
            }}
          >
            <option value="no-stretch">No Stretch</option>
            <option value="small">Small (up to 40 cm)</option>
            <option value="medium">Medium (41 to 80 cm)</option>
            <option value="large">Large (81 to 120 cm)</option>
            <option value="extra-large">Extra Large (121 to 150 cm)</option>
          </select>
        </div>

        {/* FRAME */}
        <div
          className="print-option-row frame-row"
        >
          <label className="form-label">Frame</label>
          <div className="frame-dropdown-display" onClick={() => setFrameOpen(true)}>
            <span>{selectedFrame?.name || 'Select Frame'}</span>
            <span className="frame-arrow">›</span>
          </div>
        </div>

        {/* MATS */}
        {[0, 1, 2].map(i => (
          (i === 0 || hasMat(i - 1)) && (
            <div key={i}>
              <div className="print-option-row mat-row">
                <label className="form-label">Mat {i + 1}</label>
                <div
                  className="frame-dropdown-display"
                  onClick={() => {
                    setActiveMatIndex(i);
                    setMatOpen(true);
                  }}
                >
                  <span>{mats[i].mat?.name || 'Select Mat'}</span>
                  <span className="frame-arrow">›</span>
                </div>
              </div>

              {mats[i].mat && (
                <>
                  {/* Mode Dropdown */}
                  <div className="print-option-row">
                    <label className="form-label">Mat {i + 1} Size Mode</label>
                    <select
                      className="form-select"
                      value={mats[i].mode}
                      onChange={e =>
                        updateMat(i, { mode: e.target.value })
                      }
                    >
                      <option value="equal">Equal Size</option>
                      <option value="custom">Custom Size</option>
                    </select>
                  </div>

                  {/* Equal Mode */}
                  {mats[i].mode === 'equal' && (
                    <div className="print-option-row">
                      <label className="form-label">
                        Width: <strong>{mats[i].thickness} cm</strong>
                      </label>
                      <input
                        type="range"
                        min={MAT_MIN_CM}
                        max={MAT_MAX_CM}
                        step={MAT_STEP_CM}
                        value={mats[i].thickness}
                        onChange={e =>
                          updateMat(i, { thickness: Number(e.target.value) })
                        }
                      />
                    </div>
                  )}

                  {/* Custom Mode */}
                  {mats[i].mode === 'custom' && (
                    <div className="custom-mat-grid">
                      {['top', 'bottom', 'left', 'right'].map(side => (
                        <div key={side} className="print-option-row">
                          <label className="form-label">
                            {side.toUpperCase()}:
                            <strong> {mats[i].custom[side]} cm</strong>
                          </label>
                          <input
                            type="range"
                            min={MAT_MIN_CM}
                            max={MAT_MAX_CM}
                            step={MAT_STEP_CM}
                            value={mats[i].custom[side]}
                            onChange={e =>
                              updateMat(i, {
                                custom: {
                                  ...mats[i].custom,
                                  [side]: Number(e.target.value)
                                }
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

            </div>
          )
        ))}

        {/* MAT SIDEBAR */}
        {matOpen && (
          <div className="frame-sidebar open">
            <div className="frame-sidebar-header">
              <h3>Mats</h3>
              <button onClick={() => setMatOpen(false)}>✕</button>
            </div>

            {matOptions.map(mat => (
              <div
                key={mat.id}
                className={`frame-item ${mats[activeMatIndex]?.mat?.id === mat.id ? 'active' : ''
                  }`}
                onClick={() => {
                  updateMat(activeMatIndex, {
                    mat,
                    thickness: mats[activeMatIndex].thickness || 2
                  });

                  setMatOpen(false);
                }}
              >
                <img src={mat.img} alt={mat.name} />
                <div className="frame-meta">
                  <div className="frame-name">{mat.name}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* V-GROOVE (only if Mat 1 exists) */}
        {hasMat(0) && (
          <div className="print-option-row">
            <label className="form-label">V-Groove</label>

            <select
              className="form-select"
              value={vGrooveType}
              onChange={e => setVGrooveType(e.target.value)}
            >
              {vGrooveOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* GLASS */}
        <div className="print-option-row">
          <label className="form-label">Glass</label>

          <select
            className="form-select"
            value={glassType}
            onChange={e => setGlassType(e.target.value)}
          >
            {glassOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* SPACER */}
        <div className="print-option-row">
          <label className="form-label">Spacer</label>

          <select
            className="form-select"
            value={spacerType}
            onChange={e => setSpacerType(e.target.value)}
          >
            {spacerOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* BACKING */}
        <div className="print-option-row">
          <label className="form-label">Backing</label>

          <select
            className="form-select"
            value={backingType}
            onChange={e => setBackingType(e.target.value)}
          >
            {backingOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Price */}
        <div className="price-cart-row">
          <div className="product-price">
            ${finalPrice.toFixed(2)} + GST (10%) ${gstAmount.toFixed(2)} = ${totalWithGST.toFixed(2)}
          </div>
          <button
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {loading ? `${progress}%` : 'Add to Cart'}
          </button>
        </div>
      </div>

      {/* FRAME SIDEBAR */}
      {frameOpen && (
        <div className="frame-sidebar open">
          <div className="frame-sidebar-header">
            <h3>Frames</h3>
            <button onClick={() => setFrameOpen(false)}>✕</button>
          </div>

          {frameOptions.map(frame => (
            <div
              key={frame.id}
              className={`frame-item ${selectedFrame?.id === frame.id ? 'active' : ''}`}
              onClick={() => {
                setSelectedFrame(frame);
                setFrameOpen(false);
              }}
            >
              <img src={frame.img} alt={frame.name} />
              <div className="frame-meta">
                <div className="frame-name">{frame.name}</div>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

export default FrameYourPaperArt;